import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Container from "@/components/Container";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Coins, Hammer, BadgeCheck, Save, ExternalLink, Star, Calculator, Archive, Trash2, GitBranch } from "lucide-react";
import RefurbEstimator from "@/components/RefurbEstimator";

const STATUSES = ["new","reviewing","shortlisted","researching_area","contact_agent","contact_owner","viewing_needed","viewing_booked","viewed","offer_calculation","offer_ready","offer_sent","negotiating","survey_needed","legal_review","finance_review","purchase_agreed","acquired","renovation_planning","in_renovation","snagging","staging","ready_for_resale","listed_for_resale","under_offer","sold","rejected","archived"];

const OFFER_STATUSES = [
  { v: "", l: "—" },
  { v: "pending", l: "Pending" },
  { v: "accepted", l: "Accepted" },
  { v: "rejected", l: "Rejected" },
  { v: "countered", l: "Countered" },
  { v: "withdrawn", l: "Withdrawn" },
];

const fmt = (n) => "£" + (Number(n) || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });

export default function PropertyAdminDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/properties/${id}`).then(r => { setP(r.data); setDraft(r.data); });
  }, [id]);

  const update = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/properties/${id}`, draft);
      setP(data); setDraft(data);
      toast.success("Saved");
    } catch (e) {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  const setStatus = async (newStatus) => {
    try {
      const { data } = await api.put(`/properties/${id}`, { deal_status: newStatus });
      setP(data); setDraft(data);
      toast.success(`Moved to ${newStatus}`);
    } catch { toast.error("Failed"); }
  };

  const addToWatchlist = async () => {
    try {
      await api.post("/watchlist", { property_id: id, reason: "From property detail" });
      toast.success("Added to Watchlist");
    } catch { toast.error("Failed"); }
  };

  const deleteProperty = async () => {
    if (!confirm(`Delete "${p.title}" permanently?`)) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Deleted");
      navigate("/admin/properties");
    } catch { toast.error("Failed"); }
  };

  // ---- live computed ROI for the tracker card ----
  const live = useMemo(() => {
    const purchase = Number(draft.actual_purchase_price) || 0;
    const reno = Number(draft.actual_renovation_spend) || 0;
    const holding = Number(draft.actual_holding_costs) || 0;
    const other = Number(draft.actual_other_costs) || 0;
    const sold = Number(draft.actual_sold_price) || 0;
    const invested = purchase + reno + holding + other;
    const profit = sold > 0 ? sold - invested : 0;
    const roi = invested > 0 && sold > 0 ? (profit / invested) * 100 : 0;
    const offer = Number(draft.offer_submitted_price) || 0;
    const askingDelta = offer && draft.asking_price ? ((offer - Number(draft.asking_price)) / Number(draft.asking_price)) * 100 : 0;
    return { invested, profit, roi, offer, askingDelta };
  }, [draft]);

  if (!p) return <Container className="py-12 text-ivory/40">Loading…</Container>;

  const setField = (k, v) => setDraft({ ...draft, [k]: v });
  const setNum = (k, v) => setDraft({ ...draft, [k]: v === "" ? null : Number(v) });

  return (
    <Container className="py-8 md:py-12">
      <Link to="/admin/properties" className="btn-gold-ghost">← Property Database</Link>
      <p className="overline mt-6">{p.deal_status}</p>
      <h1 className="mt-2 text-4xl font-serif text-ivory">{p.title}</h1>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ───── LEFT: Property data ───── */}
        <div className="lg:col-span-2 space-y-6">
          <div className="luxury-card p-6 space-y-4">
            <p className="overline">Property details</p>
            <Field label="Title" value={draft.title} onChange={v => setField("title", v)} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="City" value={draft.city || ""} onChange={v => setField("city", v)} />
              <Field label="Postcode" value={draft.postcode || ""} onChange={v => setField("postcode", v)} />
              <Field label="County" value={draft.county || ""} onChange={v => setField("county", v)} />
              <Field label="Country" value={draft.country || ""} onChange={v => setField("country", v)} />
              <Field label="Asking price" value={draft.asking_price || ""} onChange={v => setNum("asking_price", v)} type="number" />
              <Field label="Estimated offer" value={draft.estimated_offer_price || ""} onChange={v => setNum("estimated_offer_price", v)} type="number" />
              <Field label="Bedrooms" value={draft.bedrooms || ""} onChange={v => setNum("bedrooms", v)} type="number" />
              <Field label="Bathrooms" value={draft.bathrooms || ""} onChange={v => setNum("bathrooms", v)} type="number" />
              <Field label="Property type" value={draft.property_type || ""} onChange={v => setField("property_type", v)} />
              <Field label="Tenure" value={draft.tenure || ""} onChange={v => setField("tenure", v)} />
              <Field label="Opportunity score" value={draft.opportunity_score || 0} onChange={v => setNum("opportunity_score", v)} type="number" />
              <Field label="Risk score" value={draft.risk_score || 0} onChange={v => setNum("risk_score", v)} type="number" />
            </div>
            <label className="block">
              <span className="overline">Description</span>
              <textarea rows={5} className="mt-2 w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" value={draft.description || ""} onChange={e => setField("description", e.target.value)} />
            </label>
            <label className="block">
              <span className="overline">Source URL</span>
              <input className="mt-2 w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" value={draft.source_url || ""} onChange={e => setField("source_url", e.target.value)} />
            </label>
          </div>

          {/* ───── REFURB ESTIMATOR (price-book driven) ───── */}
          <RefurbEstimator
            selected={draft.selected_refurb_items || []}
            onChange={(rows, total) => setDraft(d => ({ ...d, selected_refurb_items: rows, estimated_renovation_cost: total }))}
            onApplyTotal={(total) => {
              setDraft(d => ({ ...d, actual_renovation_spend: total }));
              toast.success(`Applied ${total ? "£" + total.toLocaleString("en-GB") : ""} to renovation spend`);
            }}
          />

          {/* ───── OFFER & DEAL TRACKER ───── */}
          <div className="luxury-card-ruby p-6 space-y-6" data-testid="deal-tracker">
            <div>
              <p className="overline">Offer &amp; deal tracker</p>
              <p className="text-xs text-ivory/50 mt-1">
                Log the offer you submitted, what you actually paid, what renovation cost, and what it sold for. ROI updates live.
              </p>
            </div>

            {/* OFFER SECTION */}
            <div>
              <p className="text-[0.65rem] uppercase tracking-[0.2em] text-gold mb-3">1 · Offer submitted</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Offer (£)" type="number" value={draft.offer_submitted_price || ""} onChange={v => setNum("offer_submitted_price", v)} testId="field-offer-price" />
                <Field label="Date" type="date" value={(draft.offer_submitted_date || "").slice(0,10)} onChange={v => setField("offer_submitted_date", v)} testId="field-offer-date" />
                <Select label="Status" value={draft.offer_status || ""} onChange={v => setField("offer_status", v)} options={OFFER_STATUSES} testId="field-offer-status" />
              </div>
              <label className="block mt-3">
                <span className="overline">Offer notes</span>
                <textarea rows={2} className="mt-2 w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" value={draft.offer_notes || ""} onChange={e => setField("offer_notes", e.target.value)} data-testid="field-offer-notes" />
              </label>
              {live.offer > 0 && draft.asking_price > 0 && (
                <p className="mt-2 text-[0.7rem] text-ivory/50">
                  Offer is <span className={live.askingDelta < 0 ? "text-gold" : "text-ruby-light"}>{live.askingDelta.toFixed(1)}%</span> vs asking
                  ({fmt(draft.asking_price)})
                </p>
              )}
            </div>

            {/* ACQUISITION SECTION */}
            <div className="border-t border-charcoal-600 pt-5">
              <p className="text-[0.65rem] uppercase tracking-[0.2em] text-gold mb-3">2 · Acquired</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Purchase price (£)" type="number" value={draft.actual_purchase_price || ""} onChange={v => setNum("actual_purchase_price", v)} testId="field-purchase-price" />
                <Field label="Purchase date" type="date" value={(draft.actual_purchase_date || "").slice(0,10)} onChange={v => setField("actual_purchase_date", v)} testId="field-purchase-date" />
              </div>
            </div>

            {/* RENOVATION + HOLDING SECTION */}
            <div className="border-t border-charcoal-600 pt-5">
              <p className="text-[0.65rem] uppercase tracking-[0.2em] text-gold mb-3">3 · Renovation &amp; holding</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Renovation spend (£)" type="number" value={draft.actual_renovation_spend || ""} onChange={v => setNum("actual_renovation_spend", v)} testId="field-reno-spend" />
                <Field label="Holding costs (£)" type="number" value={draft.actual_holding_costs || ""} onChange={v => setNum("actual_holding_costs", v)} testId="field-holding" />
                <Field label="Other costs (£)" type="number" value={draft.actual_other_costs || ""} onChange={v => setNum("actual_other_costs", v)} testId="field-other" />
              </div>
            </div>

            {/* SALE SECTION */}
            <div className="border-t border-charcoal-600 pt-5">
              <p className="text-[0.65rem] uppercase tracking-[0.2em] text-gold mb-3">4 · Sold</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Sold price (£)" type="number" value={draft.actual_sold_price || ""} onChange={v => setNum("actual_sold_price", v)} testId="field-sold-price" />
                <Field label="Sold date" type="date" value={(draft.actual_sold_date || "").slice(0,10)} onChange={v => setField("actual_sold_date", v)} testId="field-sold-date" />
              </div>
            </div>

            {/* LIVE TOTALS */}
            <div className="border-t border-ruby/40 pt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="deal-tracker-totals">
              <Total icon={Coins}      label="Invested"    value={fmt(live.invested)} />
              <Total icon={Hammer}     label="Reno spend"  value={fmt(draft.actual_renovation_spend)} />
              <Total icon={BadgeCheck} label="Profit"      value={live.profit ? fmt(live.profit) : "—"} accent={live.profit > 0} warn={live.profit < 0} />
              <Total icon={TrendingUp} label="ROI"         value={live.roi ? `${live.roi.toFixed(1)}%` : "—"} accent={live.roi > 0} warn={live.roi < 0} />
            </div>
          </div>
        </div>

        {/* ───── RIGHT: Sidebar ───── */}
        <div className="space-y-6">
          <div className="luxury-card p-6">
            <p className="overline">Deal Status</p>
            <select value={draft.deal_status} onChange={e => setField("deal_status", e.target.value)} data-testid="field-deal-status"
              className="mt-3 w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm">
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="luxury-card p-6 space-y-3">
            <p className="overline">Public Visibility</p>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!draft.public_visibility} onChange={e => setField("public_visibility", e.target.checked)} className="accent-ruby" />
              Show on public site
            </label>
            <select value={draft.public_status || ""} onChange={e => setField("public_status", e.target.value)} className="w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm">
              <option value="">Select public status…</option>
              <option value="in_renovation">In Renovation</option>
              <option value="coming_soon">Coming Soon</option>
              <option value="completed">Completed</option>
              <option value="sold">Sold</option>
            </select>
            <input className="w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Project name (public)" value={draft.public_project_name || ""} onChange={e => setField("public_project_name", e.target.value)} />
            <input className="w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Main image URL (public)" value={draft.public_main_image || ""} onChange={e => setField("public_main_image", e.target.value)} />
            <textarea rows={3} className="w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Short description (public)" value={draft.public_short_description || ""} onChange={e => setField("public_short_description", e.target.value)} />
          </div>
          <button onClick={update} disabled={saving} className="btn-ruby w-full justify-center disabled:opacity-50" data-testid="save-property">
            <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
          </button>

          {/* TOOLBOX */}
          <div className="luxury-card p-4 space-y-2" data-testid="property-tools">
            <p className="overline mb-2">Tools</p>
            {draft.source_url && (
              <a href={draft.source_url} target="_blank" rel="noopener noreferrer" className="btn-outline w-full justify-center" data-testid="tool-open-source">
                <ExternalLink size={13} /> Open source listing
              </a>
            )}
            <button onClick={addToWatchlist} className="btn-outline w-full justify-center" data-testid="tool-watchlist">
              <Star size={13} /> Add to Watchlist
            </button>
            <Link to={`/admin/calculator?property=${id}`} className="btn-outline w-full justify-center" data-testid="tool-calculator">
              <Calculator size={13} /> Offer Calculator
            </Link>
            <Link to="/admin/pipeline" className="btn-outline w-full justify-center" data-testid="tool-pipeline">
              <GitBranch size={13} /> Open Deal Pipeline
            </Link>
            <Link to="/admin/portfolio" className="btn-outline w-full justify-center" data-testid="link-portfolio">
              <TrendingUp size={13} /> Portfolio Performance
            </Link>
          </div>

          {/* QUICK STAGE JUMP */}
          <div className="luxury-card p-4" data-testid="property-quick-stage">
            <p className="overline mb-3">Quick stage</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setStatus("shortlisted")}    className="btn-outline !py-1.5 !text-[0.65rem] justify-center">Shortlist</button>
              <button onClick={() => setStatus("offer_sent")}      className="btn-outline !py-1.5 !text-[0.65rem] justify-center">Offer sent</button>
              <button onClick={() => setStatus("acquired")}        className="btn-outline !py-1.5 !text-[0.65rem] justify-center">Acquired</button>
              <button onClick={() => setStatus("in_renovation")}   className="btn-outline !py-1.5 !text-[0.65rem] justify-center">Renovating</button>
              <button onClick={() => setStatus("listed_for_resale")} className="btn-outline !py-1.5 !text-[0.65rem] justify-center">Listed</button>
              <button onClick={() => setStatus("sold")}            className="btn-outline !py-1.5 !text-[0.65rem] justify-center">Sold</button>
            </div>
          </div>

          {/* DANGER ZONE */}
          <div className="luxury-card p-4 border-ruby/30">
            <p className="overline mb-3">Danger zone</p>
            <button onClick={() => setStatus("archived")} className="btn-outline w-full justify-center !text-[0.65rem]" data-testid="tool-archive">
              <Archive size={12} /> Archive
            </button>
            <button onClick={deleteProperty} className="mt-2 btn-outline w-full justify-center !text-[0.65rem] !border-ruby/50 !text-ruby-light" data-testid="tool-delete">
              <Trash2 size={12} /> Delete permanently
            </button>
          </div>
        </div>
      </div>
    </Container>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="overline">{label}</span>
      <input type={type} value={value ?? ""} onChange={e => onChange(e.target.value)} data-testid={testId}
        className="mt-2 w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm text-ivory focus:border-gold focus:outline-none" />
    </label>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="overline">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
        className="mt-2 w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm text-ivory focus:border-gold focus:outline-none">
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function Total({ icon: Icon, label, value, accent, warn }) {
  const color = warn ? "text-ruby-light" : accent ? "text-gold" : "text-ivory";
  return (
    <div className="border border-charcoal-600 px-3 py-3">
      <div className="flex items-center justify-between">
        <p className="text-[0.6rem] uppercase tracking-[0.18em] text-ivory/50">{label}</p>
        <Icon size={12} className="text-ivory/40" />
      </div>
      <p className={`mt-2 font-serif text-lg ${color}`}>{value || "—"}</p>
    </div>
  );
}
